import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import {
  tradesSavedViewsController,
  getTradesSavedViewsHooks,
  toSavedSnapshot,
} from '@/features/trades/saved-views';
import { useToastStore } from '@/stores/toast-store';
import {
  isTradesPath,
  mergeTradesSearch,
  selectTradesHistoryLog,
  selectTradesViewId,
} from '@/features/trades/router-helpers';

/**
 * Bootstraps the trades saved-view from the URL on the `/trades` route
 * (LR-671) — a mirror of use-inventory-view-bootstrap.
 *
 * URL `?view_id=` is the source of truth. With no `view_id`, it resolves a
 * target (persisted activeViewId → server default → system view) and writes it
 * back to the URL; with a `view_id`, it loads that view's payload (skipping the
 * adapter.apply when it matches the persisted id so a plain F5 keeps the user's
 * domain edits).
 *
 * The write is gated so it can never cancel another route's transition: all
 * guard inputs come from the router store (window.location lags an SPA
 * transition), it bails while a transition is in flight, and re-runs once the
 * router goes idle (LR-208/LR-446/LR-511 pattern shared with use-url-view-sync).
 *
 * The write patches `view_id` over the previous search, so the counterparty
 * tab (`?counterparty=`), `mode` and the History params survive it — and
 * use-url-view-sync in turn preserves `view_id` when it swaps the tab, so the
 * two never reset each other.
 */
export function useTradesViewBootstrap(): void {
  const navigate = useNavigate();
  // Needed for the async 404 catch below: render-captured router values are
  // stale by the time getDetail() rejects, so the guard there reads router.state
  // live instead.
  const router = useRouter();

  // state.location is the router's TARGET while a transition is in flight, not
  // the route being left — so the path guard rejects as soon as a navigation
  // away from /trades starts, and isNavigating covers the window before the
  // target is known. window.location would lag both.
  const routerPath = useRouterState({ select: (s) => s.location.pathname });
  const isNavigating = useRouterState({ select: (s) => s.status === 'pending' });
  // Select primitives so the effect deps stay value-stable across re-renders.
  const isHistoryLog = useRouterState({ select: (s) => selectTradesHistoryLog(s) });
  const urlViewIdParam = useRouterState({ select: (s) => selectTradesViewId(s) });

  const savedViewsHooks = getTradesSavedViewsHooks();
  const { data: savedViewsData } = savedViewsHooks.useSavedViews();
  const loadRequestIdRef = useRef(0);
  const addToast = useToastStore((s) => s.addToast);

  const setUrlViewId = useCallback(
    (viewId: number | undefined) => {
      navigate({
        to: '/trades',
        search: (prev) => mergeTradesSearch(prev, { view_id: viewId }),
        replace: true,
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (!savedViewsData) return;
    // Never fire a competing navigate while a route transition is in flight —
    // doing so cancels the pending transition and reverts the URL. The effect
    // re-runs once the router goes idle (status is a dep).
    if (isNavigating) return;
    // Leaving /trades (incl. opening /trades/$tradeId): routerPath is already
    // the target, so this rejects before the component unmounts.
    if (!isTradesPath(routerPath)) return;
    // History → MTM / Cash Pool logs have no trades table or sidebar; leave them alone.
    if (isHistoryLog) return;
    const urlViewId = urlViewIdParam ?? null;
    const views = savedViewsData.results ?? [];
    const { useStore, loadView } = tradesSavedViewsController;
    const { service } = savedViewsHooks;
    const persistedId = useStore.getState().activeViewId;

    if (urlViewId == null) {
      const defaultView = savedViewsData.default_view_id
        ? views.find((v) => v.id === savedViewsData.default_view_id)
        : undefined;
      const persistedView = persistedId != null
        ? views.find((v) => v.id === persistedId)
        : undefined;
      const target = persistedView ?? defaultView ?? views.find((v) => v.is_system);
      if (target) setUrlViewId(target.id);
      return;
    }

    const { activeViewId, savedSnapshot } = useStore.getState();
    if (activeViewId === urlViewId && savedSnapshot !== null) return;

    // Name comes from the list synchronously so the selector doesn't flash "—".
    const meta = views.find((v) => v.id === urlViewId);
    if (meta) useStore.setState({ activeViewName: meta.name });

    // Same view as before reload: skip adapter.apply so user's domain edits
    // survive; just stash the snapshot for dirty-checking.
    const skipApply = persistedId === urlViewId;
    const requestId = ++loadRequestIdRef.current;
    service
      .getDetail(urlViewId)
      .then((detail) => {
        if (requestId !== loadRequestIdRef.current) return;
        if (skipApply) {
          useStore.setState({
            activeViewId: detail.id,
            activeViewName: detail.name,
            savedSnapshot: toSavedSnapshot(detail.payload),
            isDirty: false,
          });
        } else {
          loadView(detail.id, detail.name, detail.payload);
        }
      })
      .catch(() => {
        if (requestId !== loadRequestIdRef.current) return;
        // Mirror the sync guards for this async path: read router.state LIVE —
        // the render-captured isNavigating/routerPath are stale by the time
        // this 404 resolves (LR-511).
        if (router.state.status === 'pending' || !isTradesPath(router.state.location.pathname)) return;
        addToast('error', 'View not found or access denied');
        setUrlViewId(undefined);
      });
    // `router` (stable useRouter() instance) is listed so exhaustive-deps is
    // satisfied for the router.state read in the catch; it never changes
    // identity, so it adds no re-runs.
  }, [savedViewsData, savedViewsHooks, isHistoryLog, urlViewIdParam, routerPath, isNavigating, addToast, setUrlViewId, router]);
}
